// hooks/useStages.ts
import {
   useQuery,
   useMutation,
   useQueryClient,
   QueryKey,
} from "@tanstack/react-query";
import { Stage, StageTypes } from "@/types/productTypes";
import { stageService } from "@/clients/stageService";

export const STAGES_QUERY_KEY = (productId: number): QueryKey => [
   "stages",
   productId,
];

export function useStages(productId: number) {
   return useQuery({
      queryKey: STAGES_QUERY_KEY(productId),
      queryFn: () => stageService.getList(productId),
   });
}

export function useAddStage() {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: ({
         productId,
         relativePos,
      }: {
         productId: number;
         relativePos: number;
      }) => {
         const newStage: Omit<Stage, "id" | "options"> = {
            stageName: "New Stage",
            mainProductId: productId,
            relativePos,

            imageTemplate: [],
            threshold: 100,
            showPrice: true,
            showImageGallery: true,
            type: StageTypes.ITEM,
         };

         return stageService.create(newStage);
      },
      onSuccess: (_data, newStage) => {
         qc.invalidateQueries({
            queryKey: STAGES_QUERY_KEY(newStage.productId),
         });
      },
   });
}

export function useUpdateStage() {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: ({
         stageId,
         patch,
      }: {
         productId: number;
         stageId: number;
         patch: Partial<Stage>;
      }) => stageService.update(stageId, patch),
      onSuccess: (_data, vars) => {
         qc.invalidateQueries({
            queryKey: STAGES_QUERY_KEY(vars.productId),
         });
      },
   });
}

export function useDeleteStage() {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: ({ stageId }: { productId: number; stageId: number }) =>
         stageService.remove(stageId),
      onSuccess: (_data, vars) => {
         qc.invalidateQueries({
            queryKey: STAGES_QUERY_KEY(vars.productId),
         });
      },
   });
}

export function useReorderStages() {
   const qc = useQueryClient();
   return useMutation({
      mutationFn: ({
         id1,
         id2,
      }: {
         productId: number;
         id1: number;
         id2: number;
      }) => stageService.reorder(id1, id2),
      onSuccess: (_data, vars) => {
         qc.invalidateQueries({
            queryKey: STAGES_QUERY_KEY(vars.productId),
         });
      },
   });
}
